import { Formik } from 'formik';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import {
  CATEGORIES,
  FontAwesomeIcon,
} from '@hivebrite/design-system/FontAwesomeIcon';

import { useMutateSuggestion } from 'hb-react/shared/components/AIFormWidget/hooks/useMutateSuggestion';
import {
  type AIFormRequest,
  type FieldSuggestion,
  type UpdateSuggestionAction,
} from 'hb-react/shared/components/AIFormWidget/types';
import {
  Select,
  Textarea,
} from 'hb-react/shared/components/formik/components/inputs';

interface RefineSuggestionFormProps {
  initialSuggestion: FieldSuggestion;
  updateSuggestion: UpdateSuggestionAction;
}

export const RefineSuggestionForm = ({
  updateSuggestion,
  initialSuggestion,
}: RefineSuggestionFormProps) => {
  const { mutateAsync } = useMutateSuggestion();

  const onSubmit = async (values) => {
    const response = await mutateAsync(values);
    const { suggestions } = response as unknown as {
      suggestions: Array<FieldSuggestion>;
    };
    const newSuggestion = suggestions[0];

    const historyEntry = {
      id: `history-${Date.now()}`,
      suggestedValue: newSuggestion.suggestedValue,
      reasoning: newSuggestion.reasoning,
      timestamp: Date.now(),
    };

    const updatedSuggestion = {
      ...newSuggestion,
      status: 'pending' as const,
      history: [...(initialSuggestion.history || []), historyEntry],
      currentHistoryIndex: initialSuggestion.history?.length || 0,
    };

    updateSuggestion({
      id: initialSuggestion.id,
      suggestedValue: updatedSuggestion.suggestedValue,
      reasoning: updatedSuggestion.reasoning,
      status: updatedSuggestion.status,
      history: updatedSuggestion.history,
      currentHistoryIndex: updatedSuggestion.currentHistoryIndex,
    });
  };

  const initialValues = {
    initialSuggestion: initialSuggestion.suggestedValue,
    refinementType: 'manual',
    prompt: '',
  } satisfies AIFormRequest;

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ submitForm, setFieldValue, values, isSubmitting }) => (
        <Box flexDirection="column" gridRowGap={3} width="100%">
          <Select
            name="refinementType"
            isClearable={false}
            label={s__('AIForm|Refinement type')}
            options={[
              { label: s__('AIForm|✏️ Manual refine'), value: 'manual' },
              { label: s__('AIForm|✂️ Shorten'), value: 'short' },
              { label: s__('AIForm|📝 Elaborate'), value: 'elaborate' },
              { label: s__('AIForm|🔄 Rephrase'), value: 'rephrase' },
              { label: s__('AIForm|🎩 More formal'), value: 'formal' },
              { label: s__('AIForm|😎 More casual'), value: 'casual' },
              { label: s__('AIForm|🛠️ More technical'), value: 'technical' },
              { label: s__('AIForm|📊 Summarize'), value: 'summarize' },
              { label: s__('AIForm|🔧 Simplify'), value: 'simplify' },
            ]}
            wrapperProps={{
              mb: 0,
            }}
            onChange={(value) => {
              // Clear textarea when switching away from manual refine
              if (value !== 'manual') {
                setFieldValue('prompt', '');
              }
            }}
          />

          {values.refinementType === 'manual' && (
            <Textarea
              name="prompt"
              label={s__('AIForm|Refinement prompt')}
              placeholder={s__(
                'AIForm|Describe how you want to refine this suggestion...',
              )}
              wrapperProps={{
                mb: 0,
              }}
            />
          )}

          <Box alignSelf="flex-start" gridColumnGap={2}>
            <Button
              icon={() => (
                <FontAwesomeIcon
                  iconName="sparkles"
                  iconType={CATEGORIES.SOLID}
                  height="16px"
                />
              )}
              isLoading={isSubmitting}
              onClick={submitForm}
            >
              {s__('AIForm|Refine')}
            </Button>
            <Button
              variant="secondary"
              isDisabled={isSubmitting}
              onClick={() =>
                updateSuggestion({
                  id: initialSuggestion.id,
                  status: 'pending',
                })
              }
            >
              {s__('AIForm|Cancel')}
            </Button>
          </Box>
        </Box>
      )}
    </Formik>
  );
};
